import { ArchiveRestore, ExternalLink, Pause, Play, Trash } from 'lucide-react'
import { Link } from 'react-router'
import { isApiError } from '../../api/client'
import { usePlanOverview } from '../../api/plan'
import { useRemoveSource } from '../../api/queries'
import type { Source, SourceStatus } from '../../api/types'
import { useUser } from '../../auth/context'
import { openExternal } from '../../lib/download'
import { PLATFORM_LABEL, formatAgo, formatCount, formatShortDate, handleLabel, plural } from '../../lib/format'
import { Button, buttonClass } from '../Button'
import { Dialog } from '../Dialog'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'
import { XBadge } from '../XBadge'

export type SourceAction = 'pause' | 'resume' | 'restore' | 'remove' | 'details'

function sourceDetails(s: Source): string[] {
  return [
    s.title,
    s.followers !== null ? `${formatCount(s.followers)} подп.` : null,
    s.last_polled_at ? `проверен ${formatAgo(s.last_polled_at)}` : 'ждёт первого обхода',
    s.added_at ? `добавлен ${formatShortDate(s.added_at)}` : null,
  ].filter((x): x is string => Boolean(x))
}


/** Свой канал или аккаунт доски контент-плана: его посты не попадают в «Залёты». */
export function OwnTag({ boardId }: { boardId: number }) {
  // С архивными: источник архивной доски по-прежнему свой и вне «Залётов».
  const overview = usePlanOverview({ archived: true })
  const board = overview.data?.boards.find((b) => b.id === boardId)
  return (
    <span className="shrink-0 truncate rounded bg-surface px-1 text-[11px] text-muted" title="Посты своего канала не попадают в «Залёты» и сводку">
      Свой{board ? ` · ${board.title}${board.archived ? ' (в архиве)' : ''}` : ''}
    </span>
  )
}

export function StateTag({ source: s }: { source: Source }) {
  if (s.state === 'active' && !s.from_analysis) return null
  return (
    <span className="shrink-0 rounded bg-surface px-1 text-[11px] text-muted">
      {s.from_analysis ? 'не отслеживается' : s.state === 'paused' ? 'на паузе' : 'убран'}
    </span>
  )
}

export function StatusDot({ status }: { status: SourceStatus }) {
  const cls =
    status === 'found'
      ? 'bg-fg'
      : status === 'error'
        ? 'bg-danger'
        : status === 'pending'
          ? 'bg-transparent ring-1 ring-muted ring-inset'
          : 'bg-muted/50'
  return <span className={`size-2 shrink-0 rounded-full ${cls}`} aria-hidden />
}

/**
 * Что значит статус — словами. На компьютере это подсказка при наведении, на
 * телефоне (наведения нет) — строка в листе «⋯».
 */
export function statusExplanation(s: Source): string {
  if (s.from_analysis) return 'Автор не отслеживается: показан ранее сохранённый контент.'
  switch (s.status) {
    case 'error':
      return s.error_reason
        ? `Ошибка: ${s.error_reason}`
        : 'Ошибка: источник недоступен или три обхода подряд закончились ошибкой.'
    case 'pending':
      return s.error_reason ?? 'Ждёт обхода: новый источник, посты появятся после ближайшего обхода.'
    case 'found': {
      const best = s.best_xn_7d !== null ? `, лучший ×${s.best_xn_7d.toFixed(1).replace('.', ',')}` : ''
      return `Есть находки: ${s.outliers_7d} ${plural(s.outliers_7d, 'залёт', 'залёта', 'залётов')} ×2+ за 7 дней${best}.`
    }
    default:
      return `Тишина: ${s.posts_7d} ${plural(s.posts_7d, 'пост', 'поста', 'постов')} за 7 дней, залётов ×2+ нет.`
  }
}

export function StatusBadge({ source: s }: { source: Source }) {
  const title = statusExplanation(s)
  if (s.status === 'error') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-danger" title={title}>
        <StatusDot status="error" />
        Ошибка
      </span>
    )
  }
  if (s.status === 'pending') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted" title={title}>
        <StatusDot status="pending" />
        Ждёт обхода
      </span>
    )
  }
  if (s.status === 'found') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 text-xs" title={title}>
        <span className="text-muted tabular-nums">
          {s.outliers_7d} {plural(s.outliers_7d, 'залёт', 'залёта', 'залётов')}
        </span>
        {s.best_xn_7d !== null && <XBadge value={s.best_xn_7d} />}
      </span>
    )
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted" title={title}>
      <StatusDot status="quiet" />
      Тишина
    </span>
  )
}


export function SourceDialog({
  source: s,
  onAction,
  onClose,
  showCardLink = true,
}: {
  source: Source
  showCardLink?: boolean
  onAction: (action: SourceAction, s: Source) => void
  onClose: () => void
}) {
  const owner = useUser().role === 'owner'
  const name = handleLabel(s.platform, s.handle)
  return (
    <Dialog title={name} onClose={onClose}>
      <p className="text-sm text-muted">
        {PLATFORM_LABEL[s.platform]}
        {s.state === 'paused' && ' · на паузе'}
        {s.state === 'removed' && ' · убран'}
      </p>
      <p className="mt-1 text-sm text-pretty">{sourceDetails(s).join(' · ')}</p>
      {s.state !== 'removed' && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-surface px-3 py-2 text-sm text-pretty ring-1 ring-line ring-inset">
          <span className="mt-1.5">
            <StatusDot status={s.status} />
          </span>
          <span className={s.status === 'error' ? 'text-danger' : ''}>{statusExplanation(s)}</span>
        </div>
      )}
      {s.state === 'removed' && s.error_reason && <p className="mt-1 text-sm text-danger">{s.error_reason}</p>}
      <div className="mt-4 flex flex-col gap-2">
        {showCardLink && <Link to={`/sources/${s.id}`} onClick={onClose} className={buttonClass('primary')}>Карточка источника</Link>}
        {!s.from_analysis && s.state === 'active' && (
          <Button onClick={() => onAction('pause', s)}>
            <Pause size={16} />
            Поставить на паузу
          </Button>
        )}
        {!s.from_analysis && s.state === 'paused' && (
          <Button variant="primary" onClick={() => onAction('resume', s)}>
            <Play size={16} />
            Вернуть в сбор
          </Button>
        )}
        {(s.state === 'removed' || s.from_analysis) && (
          <Button variant="primary" onClick={() => onAction('restore', s)}>
            <ArchiveRestore size={16} />
            {s.from_analysis ? 'Отслеживать автора' : 'Вернуть'}
          </Button>
        )}
        {s.url && (
          <Button onClick={() => openExternal(s.url ?? '')}>
            <ExternalLink size={16} />
            Открыть в {PLATFORM_LABEL[s.platform]}
          </Button>
        )}
        {owner && !s.from_analysis && s.state !== 'removed' && (
          <Button variant="ghost" onClick={() => onAction('remove', s)}>
            <span className="inline-flex items-center gap-2 text-danger">
              <Trash size={16} />
              Убрать
            </span>
          </Button>
        )}
      </div>
    </Dialog>
  )
}

export function RemoveDialog({ source: s, onClose }: { source: Source; onClose: () => void }) {
  const remove = useRemoveSource()
  const name = handleLabel(s.platform, s.handle)
  return (
    <Dialog
      title={`Убрать ${name}?`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(s.id, {
                onSuccess: () => {
                  toast(`${name} убран`)
                  onClose()
                },
              })
            }
          >
            {remove.isPending && <Spinner size={14} className="text-white" />}
            Убрать
          </Button>
        </>
      }
    >
      <p className="text-sm text-pretty">
        Сбор остановится, источник уйдёт из подборок и из списка. Посты и метрики останутся. Вернуть можно через
        «Показать убранные» внизу страницы.
      </p>
      {remove.isError && (
        <p className="mt-2 text-sm text-danger">
          {isApiError(remove.error, 403) ? 'Убирать источники может только владелец.' : 'Не получилось убрать.'}
        </p>
      )}
    </Dialog>
  )
}
